import { useCallback, useEffect, useState } from 'react';

export type SoundName = 'flag' | 'win' | 'loss' | 'levelUp';

const STORAGE_KEY = 'minesweeper.sound.v1';

/** A single, short envelope-shaped tone — the building block every effect below is made of. */
function tone(
  ctx: AudioContext,
  when: number,
  frequency: number,
  durationSec: number,
  peakGain: number,
  waveform: OscillatorType = 'sine',
): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = waveform;
  osc.frequency.value = frequency;
  osc.connect(gain);
  gain.connect(ctx.destination);

  // A fast attack and a longer decay reads as a soft "note" rather than a
  // click or a buzz — the difference between a sound that's pleasant fifty
  // times a session and one that gets the tab muted after three.
  gain.gain.setValueAtTime(0, when);
  gain.gain.linearRampToValueAtTime(peakGain, when + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, when + durationSec);

  osc.start(when);
  osc.stop(when + durationSec + 0.02);
}

let sharedCtx: AudioContext | null = null;

function getContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!sharedCtx) sharedCtx = new Ctor();
  if (sharedCtx.state === 'suspended') void sharedCtx.resume();
  return sharedCtx;
}

/**
 * Every effect is a handful of short sine/triangle notes — no samples to
 * ship, no asset loading, and nothing sharp enough to startle. "Chill" here
 * means the shape of the sound (soft attack, no harsh waveforms), not the
 * volume — these are tuned to actually be heard at a normal system volume,
 * just without ever sounding sharp or jarring while doing it. Reveals stay
 * silent on purpose: a cascade opens dozens of cells at once, and a sound per
 * cell is exactly the kind of thing that turns "nice" into "get me the mute
 * button" within one game.
 */
function playSound(name: SoundName): void {
  const ctx = getContext();
  if (!ctx) return;
  const t0 = ctx.currentTime;

  switch (name) {
    case 'flag':
      tone(ctx, t0, 720, 0.09, 0.12, 'triangle');
      break;
    case 'win':
      tone(ctx, t0, 523.25, 0.16, 0.12); // C5
      tone(ctx, t0 + 0.09, 659.25, 0.16, 0.12); // E5
      tone(ctx, t0 + 0.18, 783.99, 0.26, 0.13); // G5
      break;
    case 'levelUp':
      tone(ctx, t0, 659.25, 0.14, 0.11); // E5
      tone(ctx, t0 + 0.1, 783.99, 0.14, 0.11); // G5
      tone(ctx, t0 + 0.2, 1046.5, 0.3, 0.14); // C6
      break;
    case 'loss':
      tone(ctx, t0, 330, 0.22, 0.1, 'triangle'); // E4
      tone(ctx, t0 + 0.13, 233.08, 0.32, 0.1, 'triangle'); // A#3
      break;
  }
}

function readStored(): boolean {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === '0') return false;
  } catch {
    // Private browsing; sound stays on by default.
  }
  return true;
}

/** Sound is on by default and every call is a no-op while off — callers never branch on it. */
export function useSound(): { enabled: boolean; setEnabled: (enabled: boolean) => void; play: (name: SoundName) => void } {
  const [enabled, setEnabledState] = useState<boolean>(readStored);

  useEffect(() => {
    // Browsers refuse to start an AudioContext before a user gesture; the
    // first click/tap/keypress anywhere on the page wakes it up so the very
    // first flag or win doesn't play silently.
    const wake = (): void => {
      getContext();
      window.removeEventListener('pointerdown', wake);
      window.removeEventListener('keydown', wake);
    };
    window.addEventListener('pointerdown', wake, { once: true });
    window.addEventListener('keydown', wake, { once: true });
    return () => {
      window.removeEventListener('pointerdown', wake);
      window.removeEventListener('keydown', wake);
    };
  }, []);

  const setEnabled = useCallback((next: boolean) => {
    setEnabledState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
    } catch {
      // Not being able to remember the choice is not worth failing over.
    }
  }, []);

  const play = useCallback(
    (name: SoundName) => {
      if (enabled) playSound(name);
    },
    [enabled],
  );

  return { enabled, setEnabled, play };
}
